import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, Bookmark, Crown, Users, Play } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { countryFlag } from "@/lib/constants";
import ContentCard from "@/components/ContentCard";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/me/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" /></div>;

  const plan = data.subscription?.plan_id || "FREE";

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-black text-3xl sm:text-4xl">Hi, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-white/50 text-sm mt-1">Your personal Tazama Africa space</p>
        </div>
        <div className="glass rounded-2xl px-5 py-3 flex items-center gap-3">
          <Crown className="w-6 h-6 text-[#D4AF37]" />
          <div>
            <p className="text-xs text-white/50 uppercase tracking-wider">Current Plan</p>
            <p className="font-display font-bold text-lg">{plan === "FREE" ? "Free" : plan + " Premium"}</p>
          </div>
          <Link to="/subscriptions" data-testid="dashboard-upgrade" className="ml-2 px-4 py-2 rounded-full bg-[#E50914] hover:bg-[#FF2A3B] text-sm font-semibold transition-colors">
            {plan === "FREE" ? "Upgrade" : "Manage"}
          </Link>
        </div>
      </div>

      <Section icon={History} title="Continue Watching" testid="section-history">
        {data.history?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.history.map((h) => (
              <Link key={h.id} to={`/watch/${h.content_id}`} className="group flex gap-3 p-3 rounded-xl bg-[#1A1724] border border-white/5 hover:border-[#E50914]/40 transition-colors">
                <div className="relative w-28 h-16 rounded-lg overflow-hidden shrink-0">
                  <img src={h.content.thumbnail} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Play className="w-6 h-6 fill-white" /></div>
                  <div className="absolute bottom-0 left-0 h-1 bg-[#E50914]" style={{ width: `${h.completion_rate}%` }} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{h.content.title}</p>
                  <p className="text-xs text-white/50">{countryFlag(h.content.country)} {h.content.language}</p>
                  <p className="text-xs text-[#D4AF37] mt-1">{Math.round(h.completion_rate)}% watched</p>
                </div>
              </Link>
            ))}
          </div>
        ) : <Empty text="Nothing watched yet. Start exploring!" />}
      </Section>

      <Section icon={Bookmark} title="My List" testid="section-saved">
        {data.saved?.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {data.saved.map((it, i) => <ContentCard key={it.id} item={it} index={i} />)}
          </div>
        ) : <Empty text="Your watchlist is empty. Tap + on any title." />}
      </Section>

      <Section icon={Users} title="Creators You Follow" testid="section-following">
        {data.following?.length ? (
          <div className="flex flex-wrap gap-3">
            {data.following.map((c) => (
              <Link key={c.id} to={`/creator/${c.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-[#1A1724] border border-white/5 hover:border-[#E50914]/40 transition-colors">
                {c.avatar ? <img src={c.avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : <span className="w-10 h-10 rounded-full bg-[#D4AF37]/40" />}
                <span className="text-sm font-medium">{c.display_name}</span>
              </Link>
            ))}
          </div>
        ) : <Empty text="You're not following any creators yet." />}
      </Section>
    </div>
  );
}

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="mb-10" data-testid={testid}>
    <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2"><Icon className="w-5 h-5 text-[#E50914]" /> {title}</h2>
    {children}
  </div>
);
const Empty = ({ text }) => <p className="text-white/40 text-sm py-4">{text}</p>;
